/**
 * O painel à direita do mapa: o que o bloco escolhido faz, para mudar.
 *
 * Sem bloco escolhido, a visão geral: o que falta acertar antes de salvar,
 * cada item levando ao bloco dele. As expressões são as do serviço (JSONata):
 * o texto fixo vai entre aspas simples.
 */

import { useState, type ReactNode } from 'react';
import type { BlocosDoEditor, CampoDoEvento } from '@cliqcrm/contracts';
import { montaAutomacaoComSql, type QuemNaTela } from '@/entities/acesso/papel';
import { useOperacoesDeDados } from '@/shared/api/consultas';
import { Botao, Campo, CampoArea, CampoDeDuracao, CampoSelecao, Icone } from '@/shared/ui';
import {
  PROXIMO,
  acrescentarComeco,
  alterarBloco,
  mudarComeco,
  removerComeco,
  rotuloDaSaidaDoNo,
  saidaDaLigacao,
  saidasDoNo,
  type Configuracao,
} from '../lib/edicao';
import {
  ID_DO_COMECO,
  descreverNo,
  ehComeco,
  idDoOutroComeco,
  posicaoDoOutroComeco,
  rotuloDaSaida,
} from '../lib/mapa';
import { AgendaDoInicio } from './agenda-do-inicio';
import { CondicaoGuiada } from './condicao-guiada';
import { ComCamposDoEvento } from './inserir-campo';
import { ParametrosDaConsulta } from './parametros-da-consulta';
import { ReconhecerOEvento } from './reconhecer-o-evento';
import { ValorDoParametro } from './valor-do-parametro';
import { rotuloDoValor } from '../lib/condicao';
import { CAMINHO_DA_OPCAO_ESCOLHIDA, opcoesDosMenus } from '../lib/parametros';
import { PRAZO_SEM_RESPOSTA } from '../lib/menu';
import { RESULTADOS_DA_CONSULTA } from '../lib/resultado-da-consulta';

/**
 * Os eventos dos começos: o do gatilho e os dos outros começos (#1183). A
 * automação de agenda não tem evento.
 */
function eventosDosComecos(config: Configuracao): string[] {
  const g = config.gatilho;
  if (g.tipo !== 'evento') return [];
  return [g.evento, ...config.outros_comecos.map((o) => o.evento)];
}

/** O que só os itens presentes em todas as listas têm: o fluxo corre por qualquer começo. */
function emTodos<T>(listas: readonly (readonly T[])[], chave: (x: T) => string): T[] {
  if (listas.length === 0) return [];
  const [primeira, ...resto] = listas;
  return primeira!.filter((x) => resto.every((l) => l.some((y) => chave(y) === chave(x))));
}

/**
 * Os campos que o evento traz. Sem `evento`, os de TODOS os começos: um bloco do
 * fluxo pode ser alcançado por qualquer um, e só conta com o que todos trazem.
 */
function camposDoEvento(
  config: Configuracao,
  blocos: BlocosDoEditor | undefined,
  evento?: string,
): string[] {
  const eventos = evento ? [evento] : eventosDosComecos(config);
  return emTodos(
    eventos.map((ev) => blocos?.eventos.find((e) => e.evento === ev)?.contexto ?? []),
    (x) => x,
  );
}

/** O que os eventos dos começos trazem para as ações, pelo nome: a conversa, a oportunidade, a pessoa. */
function referenciasDoEvento(
  config: Configuracao,
  blocos: BlocosDoEditor | undefined,
): BlocosDoEditor['eventos'][number]['referencias'] {
  return emTodos(
    eventosDosComecos(config).map(
      (ev) => blocos?.eventos.find((e) => e.evento === ev)?.referencias ?? [],
    ),
    (x) => JSON.stringify(x),
  );
}

/**
 * Os campos que se comparam, com os valores que o CRM manda para escolher. A
 * opção escolhida no menu não tem lista no CRM: os valores são as opções dos
 * menus deste mapa. Sem `evento`, os comuns a todos os começos.
 */
function camposComparaveis(
  config: Configuracao,
  blocos: BlocosDoEditor | undefined,
  evento?: string,
): readonly CampoDoEvento[] {
  const eventos = evento ? [evento] : eventosDosComecos(config);
  const campos = emTodos(
    eventos.map((ev) => blocos?.eventos.find((e) => e.evento === ev)?.campos ?? []),
    (c) => c.caminho,
  );
  return campos.map((c) =>
    c.caminho === CAMINHO_DA_OPCAO_ESCOLHIDA && c.opcoes.length === 0
      ? { ...c, opcoes: opcoesDosMenus(config.grafo.nos) }
      : c,
  );
}

const AJUDA_DA_EXPRESSAO =
  "Uma expressão sobre o evento, como conversation.status. Texto fixo vai entre aspas simples, como 'Olá'.";

export function PainelDoBloco({
  config,
  blocos,
  quem,
  selecionado,
  problemas,
  casosNovos,
  aoMudar,
  aoLigar,
  aoNovoCaso,
  aoTirarCasos,
  aoEscolher,
  aoDuplicar,
  aoRemover,
}: {
  config: Configuracao;
  blocos: BlocosDoEditor | undefined;
  quem: QuemNaTela | null;
  selecionado: string | null;
  problemas: Record<string, string[]>;
  casosNovos: readonly string[];
  aoMudar: (c: Configuracao) => void;
  /** Liga uma saída a um bloco; sem `para`, desliga. */
  aoLigar: (de: string, saida: string, para: string | null) => void;
  aoNovoCaso: (id: string, caso: string) => void;
  /** Tira casos ainda sem ligação: os do campo que deixou de decidir. */
  aoTirarCasos: (id: string, casos: readonly string[]) => void;
  aoEscolher: (id: string | null) => void;
  aoDuplicar: (id: string) => void;
  /** Tira o bloco; a tela oferece desfazer. */
  aoRemover: (id: string) => void;
}): ReactNode {
  const no = config.grafo.nos.find((n) => n.id === selecionado);
  /** A posição em `outros_comecos`, quando o escolhido é um outro começo (#1183). */
  const outro = selecionado ? posicaoDoOutroComeco(selecionado) : null;
  const nomeDe = (id: string): string => {
    const alvo = config.grafo.nos.find((n) => n.id === id);
    if (alvo) return descreverNo(alvo, blocos).titulo;
    return posicaoDoOutroComeco(id) !== null ? 'Também começa quando' : 'Começa quando';
  };
  const doBloco = selecionado ? (problemas[selecionado] ?? []) : [];

  if (!selecionado) {
    const lista = Object.entries(problemas);
    return (
      <aside className="editor-do-mapa__painel" aria-label="Configuração">
        <h2 className="editor-do-mapa__painel-titulo">Visão geral</h2>
        <p className="editor-do-mapa__painel-ajuda">
          Clique num bloco para ver e mudar o que ele faz. Para ligar, arraste da saída de um bloco
          até a entrada de outro, ou escolha o destino de cada saída no painel do bloco. Delete ou
          Backspace tira o bloco ou a ligação escolhida.
        </p>
        {lista.length === 0 ? (
          <p className="editor-do-mapa__painel-ok">Nada falta para salvar.</p>
        ) : (
          <ul className="editor-do-mapa__pendencias">
            {lista.map(([id, textos]) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() =>
                    aoEscolher(
                      ehComeco(id) || config.grafo.nos.some((n) => n.id === id) ? id : ID_DO_COMECO,
                    )
                  }
                >
                  {nomeDe(id)}
                </button>
                : {textos.join(' ')}
              </li>
            ))}
          </ul>
        )}
      </aside>
    );
  }

  return (
    <aside className="editor-do-mapa__painel" aria-label="Configuração">
      <h2 className="editor-do-mapa__painel-titulo">
        {selecionado === ID_DO_COMECO
          ? 'Começa quando'
          : outro !== null
            ? 'Também começa quando'
            : no
              ? descreverNo(no, blocos).titulo
              : ''}
      </h2>

      {doBloco.length > 0 && (
        <ul className="editor-do-mapa__pendencias" aria-label="O que falta neste bloco">
          {doBloco.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      )}

      <div className="editor-do-mapa__campos">
        {selecionado === ID_DO_COMECO && (
          <CamposDoComeco
            config={config}
            blocos={blocos}
            aoMudar={aoMudar}
            aoEscolher={aoEscolher}
          />
        )}
        {outro !== null && config.outros_comecos[outro] && (
          <CamposDoOutroComeco
            key={selecionado}
            config={config}
            posicao={outro}
            blocos={blocos}
            aoMudar={aoMudar}
          />
        )}
        {no && (
          <CamposDoBloco
            // Um painel por bloco: o modo e o rascunho de um não passam para o outro.
            key={no.id}
            config={config}
            id={no.id}
            blocos={blocos}
            quem={quem}
            casosNovos={casosNovos}
            aoMudar={aoMudar}
            aoNovoCaso={aoNovoCaso}
            aoTirarCasos={aoTirarCasos}
          />
        )}
      </div>

      {/* Ligar pelo teclado: o destino de cada saída, num seletor. */}
      <fieldset className="editor-do-mapa__ligacoes">
        <legend>Para onde vai</legend>
        {(ehComeco(selecionado)
          ? [PROXIMO]
          : no
            ? saidasDoNo(no, config.grafo.ligacoes, blocos, casosNovos)
            : []
        ).map((saida) => {
          const entradaDoOutro = outro !== null ? config.outros_comecos[outro]?.entrada : undefined;
          const atual =
            selecionado === ID_DO_COMECO
              ? config.grafo.inicio
              : outro !== null
                ? entradaDoOutro === null
                  ? config.grafo.inicio
                  : (entradaDoOutro ?? '')
                : (config.grafo.ligacoes.find(
                    (l) => l.de === selecionado && saidaDaLigacao(l) === saida,
                  )?.para ?? '');
          return (
            <CampoSelecao
              key={saida}
              rotulo={
                selecionado === ID_DO_COMECO
                  ? 'Primeiro bloco'
                  : outro !== null
                    ? 'Entra no bloco'
                    : `Saída ${rotuloDaSaidaDoNo(no, saida)}`
              }
              tamanho="miudo"
              value={atual}
              onChange={(e) => aoLigar(selecionado, saida, e.target.value || null)}
            >
              <option value="">Nenhum: o caminho termina aqui</option>
              {config.grafo.nos
                .filter((n) => n.id !== selecionado)
                .map((n) => (
                  <option key={n.id} value={n.id}>
                    {nomeDe(n.id)} ({n.id})
                  </option>
                ))}
            </CampoSelecao>
          );
        })}
      </fieldset>

      {outro !== null && (
        <div className="editor-do-mapa__painel-acoes">
          <Botao
            variante="perigo"
            miudo
            onClick={() => {
              aoMudar(removerComeco(config, outro));
              aoEscolher(ID_DO_COMECO);
            }}
          >
            <Icone nome="lixeira" />
            Tirar este começo
          </Botao>
        </div>
      )}

      {no && (
        <div className="editor-do-mapa__painel-acoes">
          <Botao
            variante="secundario"
            miudo
            onClick={() => aoDuplicar(no.id)}
            desabilitado={no.tipo === 'para_cada'}
            motivo="O “para cada” não se duplica: os blocos de dentro dele teriam o mesmo nome."
          >
            <Icone nome="copiar" />
            Duplicar
          </Botao>
          <Botao variante="perigo" miudo onClick={() => aoRemover(no.id)}>
            <Icone nome="lixeira" />
            Remover
          </Botao>
        </div>
      )}
    </aside>
  );
}

function CamposDoComeco({
  config,
  blocos,
  aoMudar,
  aoEscolher,
}: {
  config: Configuracao;
  blocos: BlocosDoEditor | undefined;
  aoMudar: (c: Configuracao) => void;
  aoEscolher: (id: string | null) => void;
}): ReactNode {
  const g = config.gatilho;
  const eventos = (blocos?.eventos ?? []).filter(
    (e) => !e.em_breve || (g.tipo === 'evento' && e.evento === g.evento),
  );
  return (
    <>
      {g.tipo === 'evento' ? (
        <CampoSelecao
          rotulo="Quando"
          ajuda="Para trocar por uma rotina agendada, escolha na paleta, em “Começa quando”."
          value={g.evento}
          onChange={(e) =>
            // Outro evento, outros campos: o "Só se" de antes não valeria.
            aoMudar({
              ...config,
              gatilho: { tipo: 'evento', evento: e.target.value },
              condicao: e.target.value === g.evento ? config.condicao : null,
            })
          }
        >
          {eventos.map((e) => (
            <option key={e.evento} value={e.evento}>
              {e.titulo}
            </option>
          ))}
        </CampoSelecao>
      ) : g.tipo === 'agenda' ? (
        <AgendaDoInicio agenda={g} aoMudar={(agenda) => aoMudar({ ...config, gatilho: agenda })} />
      ) : (
        <p className="editor-do-mapa__painel-ajuda">
          Outra automação do mesmo modelo dispara esta.
        </p>
      )}
      {/* A rotina não tem evento: não há o que o "Só se" leia. */}
      {g.tipo === 'evento' && (
        <CondicaoGuiada
          key={g.evento}
          rotulo="Só se (opcional)"
          ajuda="Escolha o que o evento precisa ter para a automação seguir."
          campos={camposComparaveis(config, blocos, g.evento)}
          contexto={camposDoEvento(config, blocos, g.evento)}
          valor={config.condicao?.expressao ?? ''}
          aoMudar={(v) => aoMudar({ ...config, condicao: v.trim() ? { expressao: v } : null })}
        />
      )}
      {/*
       * Outro começo (#1183): outro evento entrando no mesmo fluxo, por qualquer
       * bloco. A rotina agendada tem um começo só.
       */}
      {g.tipo === 'evento' && (
        <div className="editor-do-mapa__painel-acoes">
          <Botao
            variante="secundario"
            miudo
            onClick={() => {
              const proxima = acrescentarComeco(config, eventoDoNovoComeco(config, blocos));
              aoMudar(proxima);
              aoEscolher(idDoOutroComeco(proxima.outros_comecos.length - 1));
            }}
          >
            <Icone nome="mais" />
            Outro começo
          </Botao>
        </div>
      )}
    </>
  );
}

/**
 * O evento de um começo novo: o primeiro do catálogo que nenhum começo usa
 * ainda — a "conversa reaberta" para quem volta, quando a URA começa pela
 * conversa nova.
 */
function eventoDoNovoComeco(config: Configuracao, blocos: BlocosDoEditor | undefined): string {
  const usados = new Set(eventosDosComecos(config));
  const disponiveis = (blocos?.eventos ?? []).filter((e) => !e.em_breve);
  return (
    disponiveis.find((e) => e.evento === 'conversa.situacao_mudou' && !usados.has(e.evento))
      ?.evento ??
    disponiveis.find((e) => !usados.has(e.evento))?.evento ??
    disponiveis[0]?.evento ??
    'conversa.situacao_mudou'
  );
}

/** Um outro começo (#1183): o evento, o "Só se" dele; a entrada fica em "Para onde vai". */
function CamposDoOutroComeco({
  config,
  posicao,
  blocos,
  aoMudar,
}: {
  config: Configuracao;
  posicao: number;
  blocos: BlocosDoEditor | undefined;
  aoMudar: (c: Configuracao) => void;
}): ReactNode {
  const o = config.outros_comecos[posicao]!;
  const eventos = (blocos?.eventos ?? []).filter((e) => !e.em_breve || e.evento === o.evento);
  return (
    <>
      <p className="editor-do-mapa__painel-ajuda">
        A automação também começa por aqui, entrando no bloco escolhido em “Para onde vai”. Os
        blocos do fluxo só contam com o que todos os começos trazem.
      </p>
      <CampoSelecao
        rotulo="Quando"
        value={o.evento}
        onChange={(e) =>
          // Outro evento, outros campos: o "Só se" de antes não valeria.
          aoMudar(
            mudarComeco(config, posicao, {
              evento: e.target.value,
              condicao: e.target.value === o.evento ? o.condicao : null,
            }),
          )
        }
      >
        {eventos.map((e) => (
          <option key={e.evento} value={e.evento}>
            {e.titulo}
          </option>
        ))}
      </CampoSelecao>
      <CondicaoGuiada
        key={o.evento}
        rotulo="Só se (opcional)"
        ajuda="Escolha o que o evento precisa ter para a automação seguir por este começo."
        campos={camposComparaveis(config, blocos, o.evento)}
        contexto={camposDoEvento(config, blocos, o.evento)}
        valor={o.condicao?.expressao ?? ''}
        aoMudar={(v) =>
          aoMudar(mudarComeco(config, posicao, { condicao: v.trim() ? { expressao: v } : null }))
        }
      />
    </>
  );
}

function CamposDoBloco({
  config,
  id,
  blocos,
  quem,
  casosNovos,
  aoMudar,
  aoNovoCaso,
  aoTirarCasos,
}: {
  config: Configuracao;
  id: string;
  blocos: BlocosDoEditor | undefined;
  quem: QuemNaTela | null;
  casosNovos: readonly string[];
  aoMudar: (c: Configuracao) => void;
  aoNovoCaso: (id: string, caso: string) => void;
  aoTirarCasos: (id: string, casos: readonly string[]) => void;
}): ReactNode {
  const no = config.grafo.nos.find((n) => n.id === id)!;
  const alterar = (mudanca: Record<string, unknown>): void =>
    aoMudar(alterarBloco(config, id, mudanca));
  const [caso, definirCaso] = useState('');
  const [escrevendoEscolha, definirEscrevendoEscolha] = useState(false);
  const comSql = montaAutomacaoComSql(quem);
  const operacoes = useOperacoesDeDados('consulta', comSql && no.tipo === 'consulta_sql');
  const campos = camposDoEvento(config, blocos);
  const comparaveis = camposComparaveis(config, blocos);
  const referencias = referenciasDoEvento(config, blocos);

  switch (no.tipo) {
    case 'acao': {
      const acao = blocos?.acoes.find((a) => a.acao === no.acao);
      if (!acao || acao.parametros.length === 0) {
        return <p className="editor-do-mapa__painel-ajuda">Esta ação não pede dados.</p>;
      }
      return (
        <>
          {acao.parametros.map((p) => (
            <ValorDoParametro
              key={p.nome}
              parametro={p}
              referencias={referencias}
              contexto={campos}
              valor={no.parametros[p.nome] ?? ''}
              aoMudar={(valor) => {
                const parametros = { ...no.parametros, [p.nome]: valor };
                if (!valor) delete parametros[p.nome];
                alterar({ parametros });
              }}
            />
          ))}
          {no.acao === 'enviar_menu' && (
            <>
              <p className="editor-do-mapa__painel-ajuda">
                Cada opção vira uma saída deste bloco: arraste dela até o que acontece quando o
                cliente a escolhe.
              </p>
              <CampoDeDuracao
                rotulo="Se o cliente não responder em"
                ajuda="Opcional. Com um prazo, o bloco ganha a saída “Sem resposta”."
                valorMs={Number(no.parametros[PRAZO_SEM_RESPOSTA] ?? 0) || null}
                aoMudar={(ms) => {
                  const { [PRAZO_SEM_RESPOSTA]: _antes, ...resto } = no.parametros;
                  alterar({
                    parametros: ms === 0 ? resto : { ...resto, [PRAZO_SEM_RESPOSTA]: String(ms) },
                  });
                }}
              />
            </>
          )}
        </>
      );
    }
    case 'condicao':
      return (
        <CondicaoGuiada
          key={id}
          rotulo="A regra"
          ajuda="Quando a regra vale, segue pelo “Sim”; quando não, pelo “Não”."
          vazia="Sem regra ainda: escolha o que decide entre o “Sim” e o “Não”."
          campos={comparaveis}
          contexto={campos}
          valor={no.expressao}
          aoMudar={(v) => alterar({ expressao: v })}
        />
      );
    case 'escolha': {
      const casos = saidasDoNo(no, config.grafo.ligacoes, blocos, casosNovos).filter((s) =>
        s.startsWith('caso:'),
      );
      const valorDoCaso = (saida: string): string => saida.slice('caso:'.length);
      // Sim ou não fica para a Condição: o caso guarda texto, e o evento traz booleano.
      const decidiveis = comparaveis.filter((c) => c.tipo === 'opcao');
      const decide = decidiveis.find((c) => c.caminho === no.expressao.trim());
      /*
       * Trocar o campo que decide tira os casos que não são valores do novo, com
       * as ligações deles: um caso de "Situação" num bloco que decide por "Quem
       * escreveu" nunca casaria, e tudo cairia em "Outro caso" sem aviso. Campo
       * sem lista (a opção de um menu de outra automação) não diz o que vale: os
       * casos, escritos à mão, ficam.
       */
      const decidirPor = (caminho: string): void => {
        const novo = decidiveis.find((c) => c.caminho === caminho);
        const validos = new Set(novo?.opcoes.map((o) => o.valor) ?? []);
        const fora =
          novo && validos.size === 0 ? [] : casos.filter((c) => !validos.has(valorDoCaso(c)));
        const alterada = alterarBloco(config, id, { expressao: caminho });
        aoMudar({
          ...alterada,
          grafo: {
            ...alterada.grafo,
            ligacoes: alterada.grafo.ligacoes.filter(
              (l) => !(l.de === id && fora.includes(saidaDaLigacao(l))),
            ),
          },
        });
        if (fora.length > 0) aoTirarCasos(id, fora);
      };
      const guiada = !escrevendoEscolha && (no.expressao.trim() === '' || decide !== undefined);
      // Os valores do campo escolhido que ainda não viraram caso: um clique cada.
      const valoresDoCampo = decide?.opcoes ?? [];
      const faltam = valoresDoCampo.filter((o) => !casos.some((c) => valorDoCaso(c) === o.valor));
      return (
        <>
          {guiada ? (
            <>
              <CampoSelecao
                rotulo="Decidir por"
                ajuda="Cada valor do campo vira um caminho; o que não bate vai para “Outro caso”."
                value={decide?.caminho ?? ''}
                onChange={(e) => decidirPor(e.target.value)}
              >
                <option value="">Escolha o campo</option>
                {decidiveis.map((c) => (
                  <option key={c.caminho} value={c.caminho}>
                    {c.titulo}
                  </option>
                ))}
              </CampoSelecao>
              <Botao variante="fantasma" miudo onClick={() => definirEscrevendoEscolha(true)}>
                Escrever a expressão
              </Botao>
            </>
          ) : (
            <ComCamposDoEvento
              campos={campos}
              valor={no.expressao}
              aoMudar={(v) => alterar({ expressao: v })}
            >
              {(ref) => (
                <CampoArea
                  ref={ref}
                  rotulo="O valor que decide"
                  ajuda={`Cada caso abaixo é um valor possível; o que não bate vai para "Outro caso". ${AJUDA_DA_EXPRESSAO}`}
                  rows={3}
                  spellCheck={false}
                  value={no.expressao}
                  onChange={(e) => alterar({ expressao: e.target.value })}
                />
              )}
            </ComCamposDoEvento>
          )}
          <div className="editor-do-mapa__casos">
            <p className="editor-do-mapa__painel-ajuda">
              {casos.length === 0
                ? 'Nenhum caso ainda.'
                : `Casos: ${casos
                    .map((c) => (decide ? rotuloDoValor(decide, valorDoCaso(c)) : rotuloDaSaida(c)))
                    .join(', ')}.`}{' '}
              Um caso novo vira uma saída do bloco: arraste dela até o próximo bloco.
            </p>
            {decide && faltam.length > 0 && (
              <div className="editor-do-mapa__valores" role="group" aria-label="Valores do campo">
                {faltam.map((o) => (
                  <Botao
                    key={o.valor}
                    variante="secundario"
                    miudo
                    onClick={() => aoNovoCaso(id, `caso:${o.valor}`)}
                  >
                    <Icone nome="mais" />
                    {o.rotulo}
                  </Botao>
                ))}
              </div>
            )}
            {(!decide || valoresDoCampo.length === 0) && (
              <>
                <Campo
                  rotulo="Caso novo"
                  value={caso}
                  maxLength={100}
                  onChange={(e) => definirCaso(e.target.value)}
                />
                <Botao
                  variante="secundario"
                  miudo
                  desabilitado={!caso.trim()}
                  motivo="Escreva o valor do caso."
                  onClick={() => {
                    aoNovoCaso(id, `caso:${caso.trim()}`);
                    definirCaso('');
                  }}
                >
                  Adicionar caso
                </Botao>
              </>
            )}
          </div>
        </>
      );
    }
    case 'esperar_tempo':
      return (
        <CampoDeDuracao
          rotulo="Esperar quanto tempo"
          valorMs={no.milissegundos}
          aoMudar={(ms) => alterar({ milissegundos: ms })}
        />
      );
    case 'esperar_evento': {
      // O que o CRM ainda não envia não se espera, salvo o que o bloco já tem.
      const eventos = (blocos?.eventos ?? []).filter((e) => !e.em_breve || e.evento === no.evento);
      return (
        <>
          <CampoSelecao
            rotulo="Esperar até"
            value={no.evento}
            onChange={(e) => alterar({ evento: e.target.value })}
          >
            {eventos.map((e) => (
              <option key={e.evento} value={e.evento}>
                {e.titulo}
              </option>
            ))}
          </CampoSelecao>
          <ReconhecerOEvento
            referencias={referencias}
            campos={campos}
            valor={no.correlacao}
            aoMudar={(v) => alterar({ correlacao: v })}
          />
          <CampoDeDuracao
            rotulo="Desistir depois de (opcional)"
            ajuda="Em branco (00:00:00), espera sem prazo."
            valorMs={no.limite_ms ?? null}
            aoMudar={(ms) => alterar({ limite_ms: ms > 0 ? ms : undefined })}
          />
        </>
      );
    }
    case 'consulta_sql': {
      const usaveis = (operacoes.data?.itens ?? []).filter(
        (o) => o.situacao === 'publicada' && o.tipo === 'leitura',
      );
      const escolhida = usaveis.find((o) => o.id === no.operacao_id);
      return (
        <>
          <CampoSelecao
            rotulo="A consulta"
            value={no.operacao_id}
            disabled={!comSql || operacoes.isPending}
            onChange={(e) => {
              // A consulta nova pede os valores dela, na ordem que ela declara.
              const escolhida = usaveis.find((o) => o.id === e.target.value);
              alterar({
                operacao_id: e.target.value,
                parametros: (escolhida?.parametros ?? []).map(() => ''),
              });
            }}
          >
            <option value="">{operacoes.isPending ? 'Carregando…' : 'Escolha a consulta'}</option>
            {usaveis.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nome}
              </option>
            ))}
          </CampoSelecao>
          {escolhida && (
            <ParametrosDaConsulta
              parametros={escolhida.parametros}
              valores={no.parametros}
              campos={campos}
              aoMudar={(parametros) => alterar({ parametros })}
            />
          )}
          <CampoSelecao
            rotulo="Guardar as linhas em"
            ajuda="Onde os próximos blocos acham as linhas. Cada consulta do fluxo guarda num lugar."
            value={no.resultado_em}
            onChange={(e) => alterar({ resultado_em: e.target.value })}
          >
            {/* A automação antiga, de antes da lista, mostra o nome que já tem. */}
            {!RESULTADOS_DA_CONSULTA.some((r) => r.valor === no.resultado_em) && (
              <option value={no.resultado_em}>{no.resultado_em}</option>
            )}
            {RESULTADOS_DA_CONSULTA.map((r) => (
              <option key={r.valor} value={r.valor}>
                {r.rotulo}
              </option>
            ))}
          </CampoSelecao>
        </>
      );
    }
    case 'para_cada':
      return (
        <p className="editor-do-mapa__painel-ajuda">
          O &quot;para cada&quot; carrega um mapa dentro de si, que este editor ainda não mostra.
          Ele continua como está ao salvar.
        </p>
      );
  }
}
